import {
  BankAccount,
  TransactionPaymentMethod,
  TransactionType,
} from '__generated__/GQL';
import { getBankAccountDisplayName } from 'common/bankAccount/utils';
import { TransactionEnrichmentFragment } from 'common/graphql/fragments/transactionEnrichment.gql';
import { capitalize } from 'helpers/string';

import locales from './locales';

interface LabelResult {
  preposition?: string;
  noun: string;
}

type PartialBankAccount = Pick<BankAccount, 'name' | 'type'>;

type TransactionLabelProps = {
  paymentMethod: TransactionPaymentMethod;
  title: string;
  transferPayinSenderName?: string;
  type: string;
  enrichment?: TransactionEnrichmentFragment | null;
};

const MAIN_ACCOUNT_NAME = 'Compte Principal';

export class TransactionLabelBuilder {
  private bankAccount?: PartialBankAccount;

  static fromTransaction(transaction: TransactionLabelProps) {
    return new TransactionLabelBuilder(transaction);
  }

  constructor(private transaction: TransactionLabelProps) {}

  private getTransactionTitleLocalized() {
    const { title } = this.transaction;
    return title === MAIN_ACCOUNT_NAME ? locales.mainAccount : title;
  }

  private getPayoutTransactionLabel(): LabelResult | null {
    const { enrichment, paymentMethod, title } = this.transaction;
    const enrichmentName = enrichment ? enrichment.name : null;
    const label = (enrichmentName || title).trim();

    if (label === '') {
      return null;
    }

    switch (paymentMethod) {
      case TransactionPaymentMethod.Check:
      case TransactionPaymentMethod.DirectDebit:
      case TransactionPaymentMethod.InstantTransfer:
      case TransactionPaymentMethod.Transfer:
        return { noun: label, preposition: locales.to };
      case TransactionPaymentMethod.IntraCompanyFundsTransfer:
        return {
          noun: this.getTransactionTitleLocalized(),
          preposition: locales.toWallet,
        };
      case TransactionPaymentMethod.WalletTransfer:
        if (title) {
          return { noun: title };
        }
        if (!enrichmentName) {
          return null;
        }
        return { noun: title || enrichmentName };
      case TransactionPaymentMethod.Card:
      default:
        return { noun: label };
    }
  }

  private getPayinTransactionLabel(): LabelResult | null {
    const { enrichment, paymentMethod, title, transferPayinSenderName } =
      this.transaction;
    const labelTransfer =
      (enrichment && enrichment.name) || transferPayinSenderName;
    const labelCard = (enrichment && enrichment.name) || title;

    if (
      paymentMethod === TransactionPaymentMethod.InstantTransfer ||
      paymentMethod === TransactionPaymentMethod.Transfer ||
      paymentMethod === TransactionPaymentMethod.SwiftTransfer ||
      (paymentMethod === TransactionPaymentMethod.WalletTransfer &&
        labelTransfer)
    ) {
      if (!labelTransfer) {
        return null;
      }

      return {
        noun: labelTransfer,
        preposition: locales.from,
      };
    }

    if (paymentMethod === TransactionPaymentMethod.Card) {
      return labelCard
        ? { noun: labelCard, preposition: locales.from }
        : { noun: locales.cardPayment };
    }

    if (paymentMethod === TransactionPaymentMethod.Check) {
      return title
        ? { noun: title, preposition: locales.from }
        : { noun: locales.checkPayment };
    }

    if (paymentMethod === TransactionPaymentMethod.WalletTransfer) {
      return title ? { noun: title } : null;
    }

    if (paymentMethod === TransactionPaymentMethod.CashDeposit) {
      return { noun: locales.cashDeposit };
    }

    if (paymentMethod === TransactionPaymentMethod.IntraCompanyFundsTransfer) {
      return {
        noun: this.getTransactionTitleLocalized(),
        preposition: locales.fromWallet,
      };
    }

    return null;
  }

  /**
   * Setting the target bank account allows to
   * enrich the label being generated.
   *
   * i.e.: "From Main account to URSSAF"
   */
  setTargetBankAccount(bankAccount?: PartialBankAccount) {
    this.bankAccount = bankAccount;
    return this;
  }

  build() {
    const { paymentMethod, type } = this.transaction;

    const isPayout = type === TransactionType.Payout;
    const labelResult = isPayout
      ? this.getPayoutTransactionLabel()
      : this.getPayinTransactionLabel();

    if (!labelResult) {
      return null;
    }

    const { noun, preposition } = labelResult;

    if (this.bankAccount) {
      const bankAccountName = getBankAccountDisplayName(this.bankAccount);

      const isIntraCompanyFundsTransfer =
        paymentMethod === TransactionPaymentMethod.IntraCompanyFundsTransfer;
      const payoutKey = isIntraCompanyFundsTransfer
        ? 'payoutLabelWallet'
        : 'payoutLabel';

      return isPayout
        ? locales.formatString(locales[payoutKey] as string, {
            receiver: noun,
            targetBankAccount: bankAccountName,
          })
        : locales.formatString(locales.payinLabel, {
            sender: `${
              preposition ? capitalize(locales.from) : ''
            } **${noun}`.trim(),
            targetBankAccount: bankAccountName,
          });
    }

    if (!preposition) {
      return noun;
    }

    return `${capitalize(preposition)} ${noun}`;
  }

  toString() {
    return this.build();
  }
}
